import { useState } from "react";
import { LegendHeadshot } from "@legends/ui";
import type { LegendLite } from "./api";
import type { LegendWithMeta } from "./legendDiscovery";
import { monoLabelStyle } from "./sections";

export function FeaturedLegendCard({ legend, onEnter }: { legend: LegendLite; onEnter: (legend: LegendLite) => void }) {
  const [hovered, setHovered] = useState(false);
  // Every Legend's public destination IS their Cave (founder terminology, LSC-381):
  // the card opens the Cave, so the CTA is always "Enter Cave" — no mixed "View
  // Profile" wording (superseding the ADR 0049 label split). A Legend without a
  // live broadcast-cave still opens gracefully to their Cave page (CavePublicScreen
  // renders the roster profile / generic storefront — the open behavior is unchanged).
  return (
    <button
      type="button"
      aria-label={`Enter ${legend.name}'s Cave`}
      onClick={() => onEnter(legend)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        width: "100%",
        padding: 0,
        textAlign: "left",
        color: "inherit",
        font: "inherit",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        borderRadius: "var(--radius-lg)",
        border: `1px solid ${hovered ? "var(--gold)" : "var(--border)"}`,
        background: "var(--surface-raised)",
        boxShadow: hovered ? "var(--glow-accent)" : "var(--shadow-raised)",
        overflow: "hidden",
        transition: "all 0.22s ease-in-out",
        transform: hovered ? "translateY(-4px)" : "none",
      }}
    >
      <div
        style={{
          height: 140,
          background: "linear-gradient(135deg, rgba(201, 162, 39, 0.2) 0%, rgba(26, 29, 33, 0.95) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          borderBottom: "1px solid var(--hairline)",
        }}
      >
        <LegendHeadshot photo={legend.photo} name={legend.name} initials={legend.initials} variant="featured" />
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            padding: "3px 8px",
            borderRadius: 4,
            background: "rgba(18, 20, 24, 0.8)",
            border: "1px solid var(--gold)",
            color: "var(--gold)",
            ...monoLabelStyle,
            fontSize: "10px",
          }}
        >
          FEATURED
        </div>
      </div>
      <div style={{ padding: "16px 18px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "22px",
            color: "var(--text-hi)",
            fontWeight: 700,
            lineHeight: 1.15,
          }}
        >
          {legend.name}
        </div>
        <div style={{ fontSize: "var(--fs-body-2)", color: "var(--text-muted)", marginTop: 4 }}>
          {legend.position}
        </div>
        <div style={{ ...monoLabelStyle, color: "var(--gold-2)", marginTop: 8, fontSize: "11px" }}>
          {legend.years}
        </div>
        <div
          style={{
            marginTop: "auto",
            paddingTop: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{
              ...monoLabelStyle,
              color: hovered ? "var(--gold)" : "var(--text-dim)",
              fontSize: "11px",
              transition: "color .15s",
            }}
          >
            Enter Cave
          </span>
          <span
            style={{
              color: "var(--gold)",
              fontSize: "16px",
              transform: hovered ? "translateX(4px)" : "none",
              transition: "transform .15s",
            }}
          >
            ▸
          </span>
        </div>
      </div>
    </button>
  );
}

export function LegendResultCard({ legend, onEnter }: { legend: LegendWithMeta; onEnter: (legend: LegendLite) => void }) {
  const [hovered, setHovered] = useState(false);
  // See FeaturedLegendCard: the public destination is always the Cave (LSC-381),
  // so the CTA is always "Enter Cave"; a cave-less legend still opens gracefully.
  return (
    <button
      aria-label={`Enter ${legend.name}'s Cave`}
      onClick={() => onEnter(legend)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        textAlign: "left",
        padding: 0,
        cursor: "pointer",
        border: `1px solid ${hovered ? "var(--gold)" : "var(--border-mute)"}`,
        borderRadius: "var(--radius-md)",
        overflow: "hidden",
        background: "var(--surface-2)",
        boxShadow: hovered ? "var(--glow-accent)" : "none",
        transition: "all .18s ease-in-out",
        transform: hovered ? "translateY(-3px)" : "none",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          height: 105,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(160deg, var(--stone-600), var(--stone-800))",
          borderBottom: "1px solid var(--hairline)",
          position: "relative",
        }}
      >
        <LegendHeadshot photo={legend.photo} name={legend.name} initials={legend.initials} variant="grid" />
        <div
          style={{
            position: "absolute",
            bottom: 6,
            left: 6,
            padding: "2px 6px",
            borderRadius: 3,
            background: "rgba(18, 20, 24, 0.85)",
            color: "var(--text-label)",
            ...monoLabelStyle,
            fontSize: "9px",
          }}
        >
          {legend.sport.toUpperCase()}
        </div>
      </div>
      <div style={{ padding: "10px 12px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "17px",
            color: "var(--text-hi)",
            lineHeight: 1.15,
            fontWeight: 700,
          }}
        >
          {legend.name}
        </div>
        <div style={{ fontSize: "var(--fs-2xs)", color: "var(--text-muted)", marginTop: 3 }}>
          {legend.position} · {legend.team}
        </div>
        <div
          style={{
            marginTop: "auto",
            paddingTop: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            ...monoLabelStyle,
            color: hovered ? "var(--gold)" : "var(--gold-2)",
            fontSize: "10px",
          }}
        >
          <span>ENTER CAVE</span>
          <span>▸</span>
        </div>
      </div>
    </button>
  );
}
